import {
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCallback, useEffect, useState, type ReactNode, type MouseEvent } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateRangeSelector from '../../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../../components/FilterSelectorWithApp';
import apiClient from '../../../../utils/axiosClient';
import RetentionLineChart from './RetentionLineChart';

dayjs.locale('ko');

const PERIOD_OPTIONS = [
  { label: 'D7 (최근 1주)', value: 'D7', days: 7 },
  { label: 'D14 (최근 2주)', value: 'D14', days: 14 },
  { label: 'D21 (최근 3주)', value: 'D21', days: 21 },
  { label: 'D30 (최근 1달)', value: 'D30', days: 30 },
];

const OS_OPTIONS = [
  { label: 'AOS + IOS', value: 'all' },
  { label: 'AOS', value: 'aos' },
  { label: 'IOS', value: 'ios' },
];

interface RetentionDayData {
  userCount: number;
  ticketUserCount: number;
}

interface DailyRetentionRow {
  date: string;
  newUserCount: number;
  day1?: RetentionDayData;
  day2?: RetentionDayData;
  day3?: RetentionDayData;
  day4?: RetentionDayData;
  day5?: RetentionDayData;
  day6?: RetentionDayData;
  day7?: RetentionDayData;
}

interface RetentionTrendRow {
  date: string;
  newUserCount: number;
  day1?: number;
  day2?: number;
  day3?: number;
  day4?: number;
  day5?: number;
  day6?: number;
  day7?: number;
}

interface RouletteRetentionResponse {
  retentionTrend: RetentionTrendRow[];
  dailyRetention: DailyRetentionRow[];
}

export default function RouletteRetention() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedPeriodPreset, setSelectedPeriodPreset] = useState<string>('D7');
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [periodAnchorEl, setPeriodAnchorEl] = useState<null | HTMLElement>(null);
  const [osAnchorEl, setOsAnchorEl] = useState<null | HTMLElement>(null);
  const [retentionData, setRetentionData] = useState<RouletteRetentionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...(response.data || [])]);
      })
      .catch(() => undefined);
  }, []);

  const applyPeriodPreset = useCallback((value: string) => {
    setSelectedPeriodPreset(value);
    const found = PERIOD_OPTIONS.find((option) => option.value === value);
    if (found?.days) {
      const newEnd = dayjs().subtract(1, 'day');
      const newStart = newEnd.subtract(found.days, 'day');
      setStartDate(newStart);
      setEndDate(newEnd);
    }
  }, []);

  const fetchRetentionData = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const app = selectedApp === '앱 전체' ? 'all' : selectedApp ?? 'all';
    setLoading(true);
    apiClient
      .get<RouletteRetentionResponse>('/api/v1/analytics/pointhome/roulette/retention', {
        params: {
          start_date: start,
          end_date: end,
          app_name: app,
          os_type: selectedOs,
        },
      })
      .then((response) => {
        setRetentionData(response.data);
      })
      .catch(() => {
        setRetentionData(null);
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // 첫 진입 시 조회 기간 프리셋을 D7로 적용
  useEffect(() => {
    applyPeriodPreset('D7');
    setInitialized(true);
  }, [applyPeriodPreset]);

  // 필터/기간이 준비된 뒤에만 리텐션 API 호출
  useEffect(() => {
    if (!initialized) return;
    fetchRetentionData();
  }, [initialized, fetchRetentionData]);

  const handleStartDateChange = useCallback((value: Dayjs | null) => {
    setSelectedPeriodPreset('custom');
    setStartDate(value);
  }, []);

  const handleEndDateChange = useCallback((value: Dayjs | null) => {
    setSelectedPeriodPreset('custom');
    setEndDate(value);
  }, []);

  const selectedPeriodLabel =
    PERIOD_OPTIONS.find((o) => o.value === selectedPeriodPreset)?.label ?? 'D7 (최근 1주)';
  const selectedOsLabel =
    OS_OPTIONS.find((o) => o.value === selectedOs)?.label ?? 'AOS + IOS';

  const renderFilterButton = ({
    icon,
    label,
    value,
    onClick,
  }: {
    icon: ReactNode;
    label: string;
    value: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  }) => (
    <Button
      variant="outlined"
      onClick={onClick}
      endIcon={<KeyboardArrowDownIcon />}
      sx={{
        borderColor: '#d0d7e2',
        borderRadius: 2,
        textTransform: 'none',
        color: '#1f2a37',
        minWidth: 220,
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {icon}
        <Stack direction="column" alignItems="flex-start">
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 12 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
        </Stack>
      </Stack>
    </Button>
  );

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', p: { xs: 2, md: 3 } }}>
      {/* 기간 설정 · 필터 설정 */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 260 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 180 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(app) => setSelectedApp(app ?? '앱 전체')}
          />
        </Box>
      </Box>

      {/* 앱 리스트 */}
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>
        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {/* 조회 기간, OS 필터 */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
          {renderFilterButton({
            icon: <CalendarTodayOutlinedIcon fontSize="small" />,
            label: '조회 기간',
            value: selectedPeriodLabel,
            onClick: (e) => setPeriodAnchorEl(e.currentTarget),
          })}
          {renderFilterButton({
            icon: <LaptopMacOutlinedIcon fontSize="small" />,
            label: 'OS',
            value: selectedOsLabel,
            onClick: (e) => setOsAnchorEl(e.currentTarget),
          })}
        </Stack>

        <Menu
          anchorEl={periodAnchorEl}
          open={Boolean(periodAnchorEl)}
          onClose={() => setPeriodAnchorEl(null)}
        >
          {PERIOD_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === selectedPeriodPreset}
              onClick={() => {
                setPeriodAnchorEl(null);
                applyPeriodPreset(option.value);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
        <Menu
          anchorEl={osAnchorEl}
          open={Boolean(osAnchorEl)}
          onClose={() => setOsAnchorEl(null)}
        >
          {OS_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === selectedOs}
              onClick={() => {
                setOsAnchorEl(null);
                setSelectedOs(option.value);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>

        {/* 잔존율 추이 차트 + 설명 + 테이블 */}
        <RetentionLineChart
          startDate={startDate ?? dayjs().startOf('month')}
          endDate={endDate ?? dayjs()}
          selectedApp={selectedApp}
          retentionData={retentionData}
          loading={loading}
        />
      </Box>
    </Box>
  );
}
